import { Inventory2Outlined as InventoryIcon } from "@mui/icons-material";
import {
  Box,
  GridLegacy as Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import PlaceholderImage from "@/components/common/PlaceholderImage/PlaceholderImage";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import { tableInCardSx } from "@/components/common/unifiedTableSx";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import { type EnrichedFrequentOutOfStock, useOutOfStockReport } from "@/hooks/useOutOfStockReport";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { StoreInsightsSummaryCardsRow } from "@/pages/reports/shared/StoreInsightsSummaryCardsRow";
import { useTrendCardPresentation } from "@/pages/reports/shared/useTrendCardPresentation";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import { useStoreInsightsTrendComparison } from "@/pages/store-insights/shared/useStoreInsightsTrendComparison";
import { storePerformanceService } from "@/services/StorePerformanceService";
import { CurrencyCode, formatCurrency, getStoreCurrencyIdentifiers } from "@/utils/currency";
import { formatInteger, formatPercentFromWhole } from "@/utils/number";
import {
  formatStoreDateRange,
  formatStoreDateTimeFileStamp,
  getBrowserTimezone,
} from "@/utils/storeTime";

interface OutOfStockProductGroup {
  productKey: string;
  product: EnrichedFrequentOutOfStock;
  variants: EnrichedFrequentOutOfStock[];
  storeName: string;
}

interface OutOfStockHoverZone {
  productKey: string;
  variantIndex: number;
  type: "product" | "variant";
}

type OutOfStockSortKey = "product" | "title" | "price" | "productId" | "store" | "count";

const OutOfStockReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    selectionLoading,
    period,
    customRange,
    currentDateRange,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
  } = useStoreInsightsReportFilters({ enableDeviceFilter: false });
  const {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  } = useStoreInsightsTrendComparison(period, currentDateRange);
  const comparisonRange = useMemo(
    () =>
      storePerformanceService.getTrendComparisonRange(
        currentDateRange,
        comparisonWindow,
        browserTimezone,
        anchorComparisonToWeekdays,
      ),
    [anchorComparisonToWeekdays, browserTimezone, comparisonWindow, currentDateRange],
  );

  const report = useOutOfStockReport(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    comparisonRange,
  );
  const networkLoading = getReportNetworkLoading(
    loading,
    selectionLoading,
    report.loading,
    report.tableLoading,
  );
  const contentLoading = networkLoading;
  const loadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, selectedStores],
  );
  const currentLocale = useMemo(
    () => new Intl.Locale(i18n.resolvedLanguage || i18n.language || "en-US"),
    [i18n.resolvedLanguage, i18n.language],
  );
  const [hoveredZone, setHoveredZone] = useState<OutOfStockHoverZone | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<OutOfStockSortKey>("count");
  const [currencyCode, currencySymbol] = useMemo(
    () => getStoreCurrencyIdentifiers(selectedStores),
    [selectedStores],
  );
  const recoveryRate =
    report.data.outOfStockCount > 0
      ? (report.data.recoveryCount / report.data.outOfStockCount) * 100
      : 0;
  const summaryLoading = loading || selectionLoading || report.loading;
  const hasSummaryData = !summaryLoading && !report.error;
  const trendDateRange = useMemo(() => {
    const range = formatStoreDateRange(
      comparisonRange.start,
      comparisonRange.end,
      browserTimezone,
      i18n.resolvedLanguage || i18n.language,
      { weekday: "short", month: "short", day: "numeric" },
    );
    return range.startDate === range.endDate
      ? range.startDate
      : `${range.startDate} – ${range.endDate}`;
  }, [
    browserTimezone,
    comparisonRange.end,
    comparisonRange.start,
    i18n.language,
    i18n.resolvedLanguage,
  ]);
  const trendProps = useTrendCardPresentation(trendDateRange);
  const formattedRecoveryCount = formatInteger(report.data.recoveryCount, currentLocale);
  const storeNameById = useMemo(() => {
    return new Map(selectedStores.map((store) => [store.id, store.name]));
  }, [selectedStores]);
  const groupedFrequentOutOfStocks = useMemo<OutOfStockProductGroup[]>(() => {
    const groupsByStoreAndProductId = new Map<string, OutOfStockProductGroup>();

    report.data.frequentOutOfStocks.forEach((row, index) => {
      const storeKey = row.storeId == null ? `unknown-store-${index}` : String(row.storeId);
      const productKey = `${storeKey}:${row.productId ?? `unknown-product-${index}`}`;
      const existingGroup = groupsByStoreAndProductId.get(productKey);

      if (existingGroup) {
        existingGroup.variants.push(row);
        return;
      }

      groupsByStoreAndProductId.set(productKey, {
        productKey,
        product: row,
        variants: [row],
        storeName:
          row.storeId == null ? "--" : (storeNameById.get(row.storeId) ?? `Store ${row.storeId}`),
      });
    });

    return Array.from(groupsByStoreAndProductId.values());
  }, [report.data.frequentOutOfStocks, storeNameById]);
  const sortedFrequentOutOfStocks = useMemo(() => {
    const getSortValue = (group: OutOfStockProductGroup): string | number => {
      switch (orderBy) {
        case "price":
          return Number(group.product.price) || 0;
        case "count":
          return group.variants.reduce((total, variant) => total + variant.count, 0);
        case "store":
          return group.storeName;
        case "productId":
          return group.product.productId ?? "";
        case "product":
        case "title":
          return group.product.productName ?? "";
      }
    };

    return [...groupedFrequentOutOfStocks].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      const comparison =
        typeof aValue === "number" && typeof bValue === "number"
          ? aValue - bValue
          : String(aValue).localeCompare(String(bValue), currentLocale.toString());

      return order === "asc" ? comparison : -comparison;
    });
  }, [currentLocale, groupedFrequentOutOfStocks, order, orderBy]);
  const maxPage = Math.max(Math.ceil(sortedFrequentOutOfStocks.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, maxPage);
  const paginatedFrequentOutOfStocks = sortedFrequentOutOfStocks.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  const formatPrice = (price: string | null) => {
    const numericPrice = price === null ? NaN : Number(price);
    if (!Number.isFinite(numericPrice)) {
      return t("storeInsights.outOfStockReport.placeholders.noPrice");
    }

    return formatCurrency(
      numericPrice,
      currencyCode as CurrencyCode,
      currencySymbol,
      currentLocale,
    );
  };

  const setProductHover = (productKey: string, variantIndex: number) => {
    setHoveredZone({ productKey, variantIndex, type: "product" });
  };

  const setVariantHover = (productKey: string, variantIndex: number) => {
    setHoveredZone({ productKey, variantIndex, type: "variant" });
  };

  const handleRequestSort = (property: OutOfStockSortKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const renderSortLabel = (property: OutOfStockSortKey, label: string) => (
    <TableSortLabel
      active={orderBy === property}
      direction={orderBy === property ? order : "asc"}
      onClick={() => handleRequestSort(property)}
    >
      {label}
      {orderBy === property ? (
        <Box component="span" sx={visuallyHidden}>
          {order === "desc"
            ? t("storeInsights.common.sortDirection.descending")
            : t("storeInsights.common.sortDirection.ascending")}
        </Box>
      ) : null}
    </TableSortLabel>
  );

  const renderTablePagination = (compact = false) => (
    <LocalizedTablePagination
      component="div"
      count={sortedFrequentOutOfStocks.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      sx={compact ? { "& .MuiToolbar-root": { minHeight: 0, px: 0 } } : undefined}
    />
  );

  const handleExport = () => {
    if (sortedFrequentOutOfStocks.length === 0) return;

    downloadCsv(
      [
        [
          t("storeInsights.outOfStockReport.fields.product"),
          t("storeInsights.outOfStockReport.fields.price"),
          t("storeInsights.outOfStockReport.fields.productId"),
          t("storeInsights.outOfStockReport.fields.store", { defaultValue: "Store" }),
          t("storeInsights.outOfStockReport.fields.color"),
          t("storeInsights.outOfStockReport.fields.size"),
          t("storeInsights.outOfStockReport.fields.size2"),
          t("storeInsights.outOfStockReport.fields.count"),
        ],
        ...sortedFrequentOutOfStocks.flatMap((group) =>
          group.variants.map((row) => [
            row.productName ?? "",
            row.price ? formatPrice(row.price) : "",
            row.productId ?? "",
            row.storeId == null ? "" : (storeNameById.get(row.storeId) ?? `Store ${row.storeId}`),
            row.color ?? "",
            row.size ?? "",
            row.size2 ?? "",
            row.count,
          ]),
        ),
      ],
      `out_of_stock_export_${formatStoreDateTimeFileStamp(new Date(), browserTimezone)}.csv`,
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <StoreInsightsReportHeader
        title={t("storeInsights.outOfStockReport.title")}
        icon={<InventoryIcon />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled
        selectedDeviceType="all"
        freshnessText={t("storeInsights.outOfStockReport.text.updatesOncePerDay")}
        showTrendSelector
        trendPeriod={comparisonWindow}
        onTrendPeriodChange={setComparisonWindow}
        trendComparisonRange={comparisonRange}
        anchorTrendComparisonToWeekdays={anchorComparisonToWeekdays}
        onAnchorTrendComparisonToWeekdaysChange={setAnchorComparisonToWeekdays}
      />

      <Box sx={{ p: { xs: 2, md: 2.5 }, pt: 0, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StoreInsightsSummaryCardsRow
              title={t("storeInsights.common.sections.requestsBreakdown")}
              cards={[
                {
                  title: t("storeInsights.outOfStockReport.cards.outOfStocks"),
                  value: hasSummaryData
                    ? formatInteger(report.data.outOfStockCount, currentLocale)
                    : "--",
                  previousText: null,
                  isPositive: false,
                  subtitle: t("storeInsights.outOfStockReport.cards.outOfStocksSubtitle"),
                  pillText: null,
                  trend: trendProps(report.outOfStockTrend, (value, locale) =>
                    formatInteger(value, locale),
                  ),
                },
                {
                  title: t("storeInsights.outOfStockReport.cards.recoveries"),
                  value: hasSummaryData
                    ? formatPercentFromWhole(recoveryRate, currentLocale)
                    : "--",
                  previousText: null,
                  isPositive: true,
                  subtitle: t("storeInsights.outOfStockReport.cards.recoveriesCountSubtitle", {
                    count: hasSummaryData ? formattedRecoveryCount : "--",
                    defaultValue: `${hasSummaryData ? formattedRecoveryCount : "--"} recoveries`,
                  }),
                  subtitleLoading: contentLoading,
                  pillText: null,
                  trend: trendProps(report.recoveryTrend, (value, locale) =>
                    formatPercentFromWhole(value, locale),
                  ),
                },
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <StoreInsightsPanel
              title={t("storeInsights.outOfStockReport.sections.frequentItems")}
              onExport={handleExport}
              exportDisabled={report.data.frequentOutOfStocks.length === 0}
              headerActions={renderTablePagination(true)}
              footer={renderTablePagination()}
            >
              {contentLoading ? (
                <Box sx={{ mb: 2.5 }}>
                  <LongRunningDataLoader
                    variant="table"
                    isLoading={true}
                    baseLabels={[
                      t("storeInsights.outOfStockReport.loading.items"),
                      t("storeInsights.common.loading.matchingData"),
                      t("storeInsights.common.loading.calculatingInsights"),
                      t("storeInsights.common.loading.rankingResults"),
                      t("storeInsights.common.loading.preparingResults"),
                    ]}
                    loopingLabels={[
                      t("storeInsights.common.loading.stillWorking"),
                      t("storeInsights.common.loading.thanksForPatience"),
                    ]}
                    fixedDurationMs={20_000}
                    locale={i18n.resolvedLanguage || i18n.language}
                    loadingKey={loadingKey}
                    estimateLabel={(duration) =>
                      t("storeComparison.loading.usuallyUnder", { duration })
                    }
                  />
                </Box>
              ) : null}
              <TableContainer>
                <Table
                  sx={{
                    ...tableInCardSx,
                    minWidth: 1200,
                    "& .MuiTableBody-root .MuiTableRow-root.out-of-stock-row:nth-of-type(odd):hover":
                      {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#111827" : "#FFFFFF",
                      },
                    "& .MuiTableBody-root .MuiTableRow-root.out-of-stock-row:nth-of-type(even):hover":
                      {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#1F2937" : "#F9FAFB",
                      },
                    "& .MuiTableBody-root .MuiTableRow-root.out-of-stock-row:hover .MuiTableCell-root":
                      {
                        backgroundColor: "inherit",
                      },
                    "& .MuiTableBody-root .MuiTableRow-root.out-of-stock-row.out-of-stock-product-hover .out-of-stock-product-cell":
                      {
                        backgroundColor: "action.hover",
                      },
                    "& .MuiTableBody-root .MuiTableRow-root.out-of-stock-row.out-of-stock-variant-hover .out-of-stock-variant-cell":
                      {
                        backgroundColor: "action.hover",
                      },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {renderSortLabel(
                          "product",
                          t("storeInsights.outOfStockReport.fields.product"),
                        )}
                      </TableCell>
                      <TableCell>
                        {renderSortLabel(
                          "title",
                          t("storeInsights.outOfStockReport.fields.title", {
                            defaultValue: "Title",
                          }),
                        )}
                      </TableCell>
                      <TableCell>
                        {renderSortLabel("price", t("storeInsights.outOfStockReport.fields.price"))}
                      </TableCell>
                      <TableCell>
                        {renderSortLabel(
                          "productId",
                          t("storeInsights.outOfStockReport.fields.productId"),
                        )}
                      </TableCell>
                      <TableCell>
                        {renderSortLabel(
                          "store",
                          t("storeInsights.outOfStockReport.fields.store", {
                            defaultValue: "Store",
                          }),
                        )}
                      </TableCell>
                      <TableCell>{t("storeInsights.outOfStockReport.fields.color")}</TableCell>
                      <TableCell>{t("storeInsights.outOfStockReport.fields.size")}</TableCell>
                      <TableCell>{t("storeInsights.outOfStockReport.fields.size2")}</TableCell>
                      <TableCell align="right">
                        {renderSortLabel("count", t("storeInsights.outOfStockReport.fields.count"))}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contentLoading ? (
                      <TableSkeletonRows columnCount={9} imageColumns={[0]} imageSize={56} />
                    ) : report.data.frequentOutOfStocks.length === 0 ? (
                      <TableRow className="table-empty-state">
                        <TableCell colSpan={9} align="center" className="py-8">
                          {report.error ? (
                            t("storeInsights.outOfStockReport.errors.failedLoad")
                          ) : (
                            <AdminEmptyState
                              icon={<InventoryIcon sx={{ fontSize: 40 }} />}
                              text={t("storeInsights.outOfStockReport.empty.noItems")}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedFrequentOutOfStocks.map((group) => (
                        <React.Fragment key={group.productKey}>
                          {group.variants.map((row, variantIndex) => (
                            <TableRow
                              className={[
                                "out-of-stock-row",
                                hoveredZone?.productKey === group.productKey &&
                                hoveredZone.type === "product"
                                  ? "out-of-stock-product-hover"
                                  : "",
                                hoveredZone?.productKey === group.productKey &&
                                hoveredZone.variantIndex === variantIndex &&
                                hoveredZone.type === "variant"
                                  ? "out-of-stock-variant-hover"
                                  : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              key={`${group.productKey}-${variantIndex}-${row.color ?? ""}-${row.size ?? ""}-${row.size2 ?? ""}`}
                              onMouseLeave={() => setHoveredZone(null)}
                            >
                              {variantIndex === 0 && (
                                <>
                                  <TableCell
                                    className="out-of-stock-product-cell"
                                    rowSpan={group.variants.length}
                                    onMouseEnter={() =>
                                      setProductHover(group.productKey, variantIndex)
                                    }
                                    sx={{ verticalAlign: "middle" }}
                                  >
                                    <PlaceholderImage
                                      alt={
                                        group.product.productName ??
                                        group.product.productId ??
                                        t(
                                          "storeInsights.outOfStockReport.placeholders.unknownProduct",
                                        )
                                      }
                                      className="h-14 w-14 rounded-md"
                                      imageUrl={group.product.imageUrl}
                                    />
                                  </TableCell>
                                  <TableCell
                                    className="out-of-stock-product-cell"
                                    rowSpan={group.variants.length}
                                    onMouseEnter={() =>
                                      setProductHover(group.productKey, variantIndex)
                                    }
                                    sx={{ verticalAlign: "middle", minWidth: 260 }}
                                  >
                                    <div className="text-gray-900" style={{ fontWeight: 500 }}>
                                      {group.product.productName ??
                                        t(
                                          "storeInsights.outOfStockReport.placeholders.unknownProduct",
                                        )}
                                    </div>
                                  </TableCell>
                                  <TableCell
                                    className="out-of-stock-product-cell"
                                    rowSpan={group.variants.length}
                                    onMouseEnter={() =>
                                      setProductHover(group.productKey, variantIndex)
                                    }
                                    sx={{ verticalAlign: "middle" }}
                                  >
                                    {formatPrice(group.product.price)}
                                  </TableCell>
                                  <TableCell
                                    className="out-of-stock-product-cell"
                                    rowSpan={group.variants.length}
                                    onMouseEnter={() =>
                                      setProductHover(group.productKey, variantIndex)
                                    }
                                    sx={{ verticalAlign: "middle" }}
                                  >
                                    {group.product.productId ?? "--"}
                                  </TableCell>
                                  <TableCell
                                    className="out-of-stock-product-cell"
                                    rowSpan={group.variants.length}
                                    onMouseEnter={() =>
                                      setProductHover(group.productKey, variantIndex)
                                    }
                                    sx={{ verticalAlign: "middle" }}
                                  >
                                    {group.storeName}
                                  </TableCell>
                                </>
                              )}
                              <TableCell
                                className="out-of-stock-variant-cell"
                                onMouseEnter={() => setVariantHover(group.productKey, variantIndex)}
                                sx={{ verticalAlign: "middle" }}
                              >
                                {row.color ?? "--"}
                              </TableCell>
                              <TableCell
                                className="out-of-stock-variant-cell"
                                onMouseEnter={() => setVariantHover(group.productKey, variantIndex)}
                                sx={{ verticalAlign: "middle" }}
                              >
                                {row.size ?? "--"}
                              </TableCell>
                              <TableCell
                                className="out-of-stock-variant-cell"
                                onMouseEnter={() => setVariantHover(group.productKey, variantIndex)}
                                sx={{ verticalAlign: "middle" }}
                              >
                                {row.size2 ?? "--"}
                              </TableCell>
                              <TableCell
                                align="right"
                                className="out-of-stock-variant-cell"
                                onMouseEnter={() => setVariantHover(group.productKey, variantIndex)}
                                sx={{ verticalAlign: "middle" }}
                              >
                                {formatInteger(row.count, currentLocale)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StoreInsightsPanel>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OutOfStockReport;
